import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch as ToggleSwitch,
  TextField,
  Typography,
  useTokens,
} from '@barracuda-internal/bds-core';
import { Delete, Edit } from '@barracuda-internal/bds-core/dist/Icons/Core';

import AADRoleGuard from 'common/components/AADRoleGuard';
import { actions } from './globalReducer';

const EMPTY_FORM = { url: '', timeout: '30', enabled: true, apiToken: '' };

/**
 * Per-account backend override management. Renders inside the account
 * detail drawer. Reads the account's overrides + the global backend
 * definitions, so support users can:
 *   - See which backends this account overrides + effective URL
 *   - Add an override for a backend that currently uses the platform default
 *   - Edit or remove an existing override (ADMIN only)
 */
const AccountBackendOverridesSection = ({ accountId }) => {
  const tokens = useTokens();
  const dispatch = useDispatch();
  const accountBackends = useSelector((s) => s.global.accountBackends);
  const backendDefinitions = useSelector((s) => s.global.backendDefinitions);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOverride, setEditingOverride] = useState(null);
  const [pickedBackendId, setPickedBackendId] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (accountId) {
      dispatch(actions.fetchAccountBackends(accountId));
      dispatch(actions.fetchBackendDefinitions());
    }
  }, [accountId, dispatch]);

  const overrides = accountBackends?.items || [];
  const definitions = backendDefinitions?.items || [];

  // For "add override", show only backends without an existing override
  const overriddenIds = new Set(overrides.map((o) => o.backend_id));
  const availableForOverride = definitions.filter((d) => !overriddenIds.has(d.id));

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingOverride(null);
    setPickedBackendId('');
    setForm(EMPTY_FORM);
  };

  const openAddDialog = () => {
    setEditingOverride(null);
    setPickedBackendId(availableForOverride[0]?.id?.toString() || '');
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEditDialog = (o) => {
    setEditingOverride(o);
    setPickedBackendId(o.backend_id.toString());
    setForm({
      url: o.url || '',
      timeout: (o.timeout ?? 30).toString(),
      enabled: o.enabled !== false,
      apiToken: '', // never populated — write-only
    });
    setDialogOpen(true);
  };

  const submit = () => {
    const payload = {
      backend_id: parseInt(pickedBackendId, 10),
      url: form.url,
      timeout: parseFloat(form.timeout) || 30,
      enabled: form.enabled,
    };
    if (form.apiToken) payload.api_token = form.apiToken;

    if (editingOverride) {
      dispatch(actions.updateAccountBackend(accountId, editingOverride.id, payload));
    } else {
      dispatch(actions.createAccountBackend(accountId, payload));
    }
    closeDialog();
  };

  const remove = (o) => {
    if (
      // eslint-disable-next-line no-alert
      window.confirm(`Remove override for ${o.backend_name}? Account will fall back to the platform default.`)
    ) {
      dispatch(actions.deleteAccountBackend(accountId, o.id));
    }
  };

  const editingBackendName =
    definitions.find((d) => d.id === parseInt(pickedBackendId, 10))?.name || '';

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: tokens.aliasesSpaceComponentDefault,
        }}
      >
        <Typography variant="captionSubtle">
          Backend overrides ({overrides.length})
        </Typography>
        <AADRoleGuard requires="ADMIN">
          {availableForOverride.length > 0 && (
            <Button
              bdsType="interactiveNeutral"
              variant="outlined"
              size="small"
              onClick={openAddDialog}
            >
              Add override
            </Button>
          )}
        </AADRoleGuard>
      </Stack>

      {overrides.length === 0 ? (
        <Typography variant="captionSubtle">
          No overrides — this account uses platform defaults for every backend.
        </Typography>
      ) : (
        <Stack sx={{ gap: tokens.aliasesSpaceComponentDefault }}>
          {overrides.map((o) => (
            <Stack
              key={o.id}
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: tokens.aliasesSpaceComponentDefault,
                padding: tokens.aliasesSpaceContainerDense,
                borderRadius: tokens.aliasesBorderRadiusDefault,
                backgroundColor: tokens.aliasesColorBgStaticContainerSubtle,
              }}
            >
              <Stack sx={{ flex: 1, minWidth: 0, gap: tokens.aliasesSpaceComponentDense }}>
                <Typography variant="body1Bold">{o.backend_name}</Typography>
                <Typography variant="captionSubtle" sx={{ wordBreak: 'break-all' }}>
                  {o.url}
                </Typography>
                <Typography variant="captionSubtle">
                  Timeout: {o.timeout ?? '—'}s
                  {o.enabled === false && ' · Disabled'}
                  {o.has_api_token && ' · API token set'}
                </Typography>
              </Stack>
              <AADRoleGuard requires="ADMIN">
                <Stack direction="row" sx={{ gap: tokens.aliasesSpaceComponentDense }}>
                  <IconButton bdsType="interactiveSubtle" size="small" onClick={() => openEditDialog(o)}>
                    <Edit />
                  </IconButton>
                  <IconButton bdsType="interactiveSubtle" size="small" onClick={() => remove(o)}>
                    <Delete />
                  </IconButton>
                </Stack>
              </AADRoleGuard>
            </Stack>
          ))}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} bdsDialogWidth="small">
        <DialogTitle
          dialogTitle={editingOverride ? `Edit override — ${editingBackendName}` : 'Add backend override'}
          showCloseIcon
          onClose={closeDialog}
        />
        <DialogContent>
          <Stack sx={{ gap: tokens.aliasesSpaceComponentDefault, marginTop: tokens.aliasesSpaceComponentDense }}>
            {!editingOverride && (
              <TextField
                select
                label="Backend"
                value={pickedBackendId}
                onChange={(e) => setPickedBackendId(e.target.value)}
                SelectProps={{ native: true }}
                fullWidth
                bdsSize="small"
              >
                {availableForOverride.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} (default: {d.default_url})
                  </option>
                ))}
              </TextField>
            )}
            <TextField
              label="Override URL"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              fullWidth
              bdsSize="small"
              placeholder="https://backend.example.com"
            />
            <TextField
              label="Timeout (seconds)"
              type="number"
              value={form.timeout}
              onChange={(e) => setForm({ ...form, timeout: e.target.value })}
              fullWidth
              bdsSize="small"
            />
            <TextField
              label={editingOverride ? 'API token (leave blank to keep existing)' : 'API token (optional)'}
              value={form.apiToken}
              onChange={(e) => setForm({ ...form, apiToken: e.target.value })}
              type="password"
              fullWidth
              bdsSize="small"
            />
            <Stack direction="row" sx={{ alignItems: 'center', gap: tokens.aliasesSpaceComponentDefault }}>
              <Typography variant="body1" sx={{ flex: 1 }}>Enabled</Typography>
              <ToggleSwitch
                checked={form.enabled}
                onChange={(_, checked) => setForm({ ...form, enabled: checked })}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button bdsType="interactiveNeutral" onClick={closeDialog}>Cancel</Button>
          <Button
            bdsType="interactiveEmphasis"
            onClick={submit}
            disabled={!pickedBackendId || !form.url}
          >
            {editingOverride ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountBackendOverridesSection;
